<script lang="ts">
  // Topbar — 1200 × 56 strip above the page content.
  //
  // Visual reference: openspec/changes/ui-redesign-v2/Untitled.fig
  //   node IDs 1:400-440.
  //
  // Renders the active route's title + subtitle, a clock pill that
  // ticks once per second, and a mirror of the WS indicator from the
  // sidebar so users who scroll the page still see the connection
  // state.

  import { onDestroy, onMount } from "svelte";
  import { resolveRoute, type RouteName } from "../lib/router";
  import { getIcon } from "../lib/icons";
  import { createWsClient } from "../lib/ws";

  let current = resolveRoute(window.location.pathname + window.location.search);
  if (typeof window !== "undefined") {
    window.addEventListener("popstate", () => {
      current = resolveRoute(window.location.pathname + window.location.search);
    });
  }

  // Connection state — share a single WS client with Sidebar via
  // module-scoped singleton (both components import the same factory).
  let connection: "connecting" | "connected" | "disconnected" = "disconnected";
  let connectionSub: (() => void) | null = null;
  let singleton: ReturnType<typeof createWsClient> | null = null;

  if (typeof window !== "undefined") {
    singleton = createWsClient();
    singleton.connect();
    connectionSub = singleton.connectionState.subscribe((next) => {
      connection = next;
    });
  }

  // Clock pill — updates every second. The mono font + tabular-nums
  // keeps the digits from jumping width.
  let now = new Date();
  let clockTimer: ReturnType<typeof setInterval> | null = null;
  onMount(() => {
    clockTimer = setInterval(() => (now = new Date()), 1000);
  });
  onDestroy(() => {
    if (clockTimer !== null) clearInterval(clockTimer);
    connectionSub?.();
  });

  function formatClock(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  function indicatorClass(state: typeof connection): string {
    return `ws-indicator ws-indicator--${state}`;
  }

  function titleForRoute(name: RouteName): string {
    switch (name) {
      case "overview": return "Overview";
      case "host": return "Host metrics";
      case "containers": return "Containers";
      case "container-detail": return "Container detail";
      case "audit": return "Audit log";
      case "login": return "Login";
    }
  }

  function subtitleForRoute(name: RouteName): string {
    switch (name) {
      case "overview": return "Cluster pulse at a glance";
      case "host": return "CPU, memory, disk, and network";
      case "containers": return "List + bulk actions";
      case "container-detail": return "Inspect, resources, logs";
      case "audit": return "Recent mutations";
      case "login": return "";
    }
  }
</script>

<header class="topbar">
  <div class="topbar__title">
    <h1>{titleForRoute(current.name)}</h1>
    <span class="topbar__subtitle">{subtitleForRoute(current.name)}</span>
  </div>
  <div class="topbar__actions">
    <span class="topbar__clock" aria-label="Current time">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d={getIcon("clock")} />
      </svg>
      <span>{formatClock(now)}</span>
    </span>
    <span class={indicatorClass(connection)} data-testid="topbar-ws-indicator" data-state={connection}>
      <span class="ws-indicator__dot" aria-hidden="true"></span>
      <span>{connection}</span>
    </span>
  </div>
</header>
